/**
 * VendorAuthService — manages vendor portal authentication state.
 *
 * RESPONSIBILITY: Holds the vendor session (vendorId, name, token, role)
 * for the vendor portal routes. In v1, the actual auth is a magic-link
 * flow via the backend; for development, a stub auth is used.
 *
 * SIGNALS:
 *   isAuthenticated (readonly)  — whether a vendor session is active and not expired
 *   isExpired (readonly)        — whether the current token has expired
 *   vendorId (readonly)         — current vendor's ID
 *   vendorName (readonly)       — current vendor's display name
 *   vendorRole (readonly)       — 'owner' | 'editor'
 *   vendorColor (readonly)      — brand colour for the vendor
 *   loading (readonly)          — loading state during auth operations
 *   error (readonly)            — error message
 *
 * USED BY: Vendor portal shell, vendor guard, vendor portal components
 * BACKEND CONTRACT: requestVendorMagicLink, verifyVendorMagicLink mutations
 *
 * AI CONTEXT: In development, stubAuth() can be called to set a mock vendor
 * session without going through the magic-link flow. Dev stub tokens never expire.
 */
import { Injectable, inject, signal, computed } from '@angular/core';
import { CateringAdminApiService } from './catering-admin-api.service';

export interface VendorSession {
  vendorId: string;
  vendorName: string;
  token: string;
  role: 'owner' | 'editor';
  color?: string;
  /** Token expiry as Unix timestamp (seconds). Null for dev stubs. */
  exp?: number | null;
  customerId?: string;
}

const VENDOR_SESSION_KEY = 'vendor-session';

@Injectable({ providedIn: 'root' })
export class VendorAuthService {
  private readonly api = inject(CateringAdminApiService);

  private readonly _session = signal<VendorSession | null>(this.loadSessionFromStorage());
  private readonly _loading = signal(false);
  private readonly _error = signal<string | null>(null);

  /** Whether the current token has expired. Dev stubs never expire. */
  readonly isExpired = computed(() => {
    const session = this._session();
    if (!session) return true;
    if (!session.exp) return false; // Dev stubs or tokens without exp never expire
    return session.exp * 1000 < Date.now();
  });

  readonly isAuthenticated = computed(() => !!this._session() && !this.isExpired());
  readonly vendorId = computed(() => this._session()?.vendorId ?? null);
  readonly vendorName = computed(() => this._session()?.vendorName ?? null);
  readonly vendorRole = computed(() => this._session()?.role ?? null);
  readonly vendorColor = computed(() => this._session()?.color ?? '#6366f1');
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();

  /** Request a magic link email for vendor sign-in */
  async requestMagicLink(email: string, customerId?: string | null): Promise<boolean> {
    this._loading.set(true);
    this._error.set(null);

    try {
      const result = await this.api.requestMagicLink(email, customerId ?? undefined);
      return result?.success ?? false;
    } catch (cause) {
      console.warn('[VendorAuth] requestMagicLink error:', cause);
      this._error.set((cause instanceof Error ? cause.message : null) || 'Failed to send sign-in link');
      return false;
    } finally {
      this._loading.set(false);
    }
  }

  /** Verify a magic link token and establish a session */
  async verifyMagicLink(token: string): Promise<boolean> {
    this._loading.set(true);
    this._error.set(null);

    try {
      const result = await this.api.verifyMagicLink(token);
      if (!result?.token) {
        this._error.set('This link has expired or already been used. Please request a new one.');
        return false;
      }

      // Decode JWT payload to extract claims
      const payload = this.decodeJwtPayload(result.token);

      const session: VendorSession = {
        vendorId: (payload?.['vendorId'] as string | undefined) ?? result.vendorId ?? '',
        vendorName: result.vendorName ?? '',
        token: result.token,
        role: (payload?.['role'] as 'owner' | 'editor') ?? 'editor',
        exp: payload?.['exp'] ?? null,
        customerId: payload?.['customerId'],
      };

      this._session.set(session);
      this.saveSessionToStorage(session);
      return true;
    } catch (cause) {
      console.warn('[VendorAuth] verifyMagicLink error:', cause);
      this._error.set((cause instanceof Error ? cause.message : null) || 'Failed to verify sign-in link');
      return false;
    } finally {
      this._loading.set(false);
    }
  }

  /** Verify a 6-digit code (sent alongside the magic link) and establish a session */
  async verifyCode(email: string, code: string, customerId?: string | null): Promise<boolean> {
    this._loading.set(true);
    this._error.set(null);

    try {
      const result = await this.api.verifyVendorCode(email, code, customerId ?? undefined);
      if (!result?.token) {
        this._error.set('Invalid or expired code. Please check and try again.');
        return false;
      }

      // Decode JWT payload to extract claims
      const payload = this.decodeJwtPayload(result.token);

      const session: VendorSession = {
        vendorId: (payload?.['vendorId'] as string | undefined) ?? result.vendorId ?? '',
        vendorName: result.vendorName ?? '',
        token: result.token,
        role: (payload?.['role'] as 'owner' | 'editor') ?? (result.role as 'owner' | 'editor') ?? 'editor',
        exp: payload?.['exp'] ?? null,
        customerId: payload?.['customerId'],
      };

      this._session.set(session);
      this.saveSessionToStorage(session);
      return true;
    } catch (cause) {
      console.warn('[VendorAuth] verifyCode error:', cause);
      this._error.set((cause instanceof Error ? cause.message : null) || 'Failed to verify code');
      return false;
    } finally {
      this._loading.set(false);
    }
  }

  /** Stub auth for development — bypasses magic link flow */
  stubAuth(vendorId: string, vendorName: string, color?: string): void {
    const session: VendorSession = {
      vendorId,
      vendorName,
      token: 'dev_stub_token',
      role: 'owner',
      color: color ?? '#6366f1',
      exp: null, // Dev stubs never expire
    };
    this._session.set(session);
    this.saveSessionToStorage(session);
  }

  /** Sign out — clear the vendor session */
  signOut(): void {
    this._session.set(null);
    try {
      localStorage.removeItem(VENDOR_SESSION_KEY);
    } catch { /* ignore */ }
  }

  /** Returns Authorization headers for vendor API calls */
  getAuthHeaders(): Record<string, string> {
    const session = this._session();
    if (!session?.token) return {};
    return { 'Authorization': `Bearer ${session.token}` };
  }

  // ── Private ─────────────────────────────────────

  /**
   * Decode the payload segment of a JWT (base64url → JSON).
   * Returns null on failure — never throws.
   */
  private decodeJwtPayload(jwt: string): Record<string, any> | null {
    try {
      const parts = jwt.split('.');
      if (parts.length !== 3) return null;
      // base64url → base64
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const json = atob(base64);
      return JSON.parse(json);
    } catch {
      return null;
    }
  }

  private loadSessionFromStorage(): VendorSession | null {
    try {
      const raw = localStorage.getItem(VENDOR_SESSION_KEY);
      if (!raw) return null;
      return JSON.parse(raw) as VendorSession;
    } catch {
      return null;
    }
  }

  private saveSessionToStorage(session: VendorSession): void {
    try {
      localStorage.setItem(VENDOR_SESSION_KEY, JSON.stringify(session));
    } catch { /* ignore */ }
  }
}
